import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Link
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { reportService } from '../../../services/api';

const RoleReadinessChart = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await reportService.getJobRoleReadiness({
                page: 0,
                limit: 4,
                search: ''
            });

            if (response.data.success) {
                const transformedData = response.data.data.map(role => ({
                    name: role.roleName,
                    readiness: role.readinessPercent
                }));
                setData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching role readiness data:', error);
            setError('Failed to load role readiness data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleClick = () => {
        navigate('/reports/job-role-readiness');
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: 1
                    }}
                >
                    <Typography variant="body2" fontWeight="medium">
                        {label}
                    </Typography>
                    <Typography variant="body2" color="primary">
                        {`Readiness: ${payload[0].value}%`}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        Role Readiness Overview
                    </Typography>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleClick}
                        sx={{ textDecoration: 'none' }}
                    >
                        View Details
                    </Link>
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                            data={data}
                            layout="vertical"
                            //margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={true} />
                            <XAxis type="number" domain={[0, 100]} />
                            <YAxis 
                                dataKey="name" 
                                type="category" 
                                width={90}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar
                                dataKey="readiness"
                                fill="var(--primary-color)"
                                radius={[0, 4, 4, 0]}
                                barSize={40}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
};

export default RoleReadinessChart;